// Particle system primitive. Renders N points spawned around a position,
// animated by per-particle velocity + gravity + drag, fading size over
// the particle's randomized lifetime.
//
// Spawn shapes:
//   point    — all particles at the origin
//   sphere   — uniform distribution on a sphere of spawnRadius
//   cone     — biased upward (for fountains / sparks)
//   ring     — distributed on a horizontal ring at spawnRadius
//   cylinder — distributed on a vertical cylinder of spawnRadius

import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import type { ParticleSystemDef } from './types.js';

interface ParticlesProps {
  readonly def: ParticleSystemDef;
  readonly position: readonly [number, number, number];
}

export function Particles({ def, position }: ParticlesProps) {
  const meshRef = useRef<THREE.Points>(null);
  const startTimeRef = useRef(performance.now());

  const { positions, velocities, lifetimes, startTimes, geometry } = useMemo(() => {
    const count = def.count;
    const positions = new Float32Array(count * 3);
    const velocities = new Float32Array(count * 3);
    const lifetimes = new Float32Array(count);
    const startTimes = new Float32Array(count);
    const [px, py, pz] = position;

    for (let i = 0; i < count; i++) {
      let dx = 0;
      let dy = 0;
      let dz = 0;
      const r = def.spawnShape === 'point' ? 0 : def.spawnRadius * Math.random();
      if (def.spawnShape === 'sphere') {
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1);
        dx = r * Math.sin(phi) * Math.cos(theta);
        dy = r * Math.cos(phi);
        dz = r * Math.sin(phi) * Math.sin(theta);
      } else if (def.spawnShape === 'ring') {
        const theta = Math.random() * Math.PI * 2;
        dx = r * Math.cos(theta);
        dz = r * Math.sin(theta);
      } else if (def.spawnShape === 'cone') {
        const theta = Math.random() * Math.PI * 2;
        const radial = r * 0.5;
        dx = radial * Math.cos(theta);
        dz = radial * Math.sin(theta);
        dy = r;
      } else if (def.spawnShape === 'cylinder') {
        const theta = Math.random() * Math.PI * 2;
        dx = r * Math.cos(theta);
        dz = r * Math.sin(theta);
        dy = (Math.random() - 0.5) * def.spawnRadius * 2;
      }
      positions[i * 3] = px + dx;
      positions[i * 3 + 1] = py + dy;
      positions[i * 3 + 2] = pz + dz;

      velocities[i * 3] = def.velocity[0] + (Math.random() - 0.5) * def.velocityJitter;
      velocities[i * 3 + 1] = def.velocity[1] + (Math.random() - 0.5) * def.velocityJitter;
      velocities[i * 3 + 2] = def.velocity[2] + (Math.random() - 0.5) * def.velocityJitter;

      lifetimes[i] = def.lifetime[0] + Math.random() * (def.lifetime[1] - def.lifetime[0]);
      startTimes[i] = 0;
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return { positions, velocities, lifetimes, startTimes, geometry: geom };
  }, [def, position]);

  const startColor = useMemo(() => new THREE.Color(def.startColor), [def.startColor]);
  const endColor = useMemo(() => new THREE.Color(def.endColor), [def.endColor]);
  const material = useMemo(() => {
    const m = new THREE.PointsMaterial({
      size: def.startSize,
      transparent: true,
      depthWrite: false,
      blending: def.blendMode === 'additive' ? THREE.AdditiveBlending : THREE.NormalBlending,
      vertexColors: false,
    });
    return m;
  }, [def.startSize, def.blendMode]);

  useFrame((_, delta) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const pos = mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
    const now = (performance.now() - startTimeRef.current) / 1000;
    let allDead = true;
    const color = new THREE.Color();
    const averageLifetime = (def.lifetime[0] + def.lifetime[1]) / 2;

    for (let i = 0; i < def.count; i++) {
      const st = startTimes[i] ?? 0;
      if (st === 0) startTimes[i] = now;
      const age = now - (startTimes[i] ?? now);
      const lt = lifetimes[i] ?? 0;
      if (age >= lt) continue;
      allDead = false;

      const ix = i * 3;
      const vx = velocities[ix] ?? 0;
      const vy = velocities[ix + 1] ?? 0;
      const vz = velocities[ix + 2] ?? 0;
      const dragFactor = 1 - def.drag * delta;
      velocities[ix] = vx * dragFactor;
      velocities[ix + 1] = (vy + def.gravity * delta) * dragFactor;
      velocities[ix + 2] = vz * dragFactor;

      const arr = pos.array as Float32Array;
      arr[ix] = (arr[ix] ?? 0) + (velocities[ix] ?? 0) * delta;
      arr[ix + 1] = (arr[ix + 1] ?? 0) + (velocities[ix + 1] ?? 0) * delta;
      arr[ix + 2] = (arr[ix + 2] ?? 0) + (velocities[ix + 2] ?? 0) * delta;
    }
    pos.needsUpdate = true;

    // Animate material size + color over the average lifetime.
    const t = Math.min(now / averageLifetime, 1);
    material.size = def.startSize + (def.endSize - def.startSize) * t;
    color.copy(startColor).lerp(endColor, t);
    material.color = color;

    if (allDead) {
      mesh.visible = false;
    }
  });

  return <points ref={meshRef} geometry={geometry} material={material} frustumCulled={false} />;
}
